import * as Cookies from 'js-cookie'

export interface Market {
  code: string;
  name: string;
  default_currency: string;
  allowed_currencies: string[];
  loyalty_enabled: boolean;
}

export const LEGACY_NORWAY_MARKET: Market = {
  code: 'no',
  name: 'Norway',
  default_currency: 'NOK',
  allowed_currencies: ['NOK'],
  loyalty_enabled: true,
}

export interface MarketSelection {
  market: Market;
  fallbackApplied: boolean;
}

export const MARKET_COOKIE_NAME = 'youwish_admin_market'
export const MARKET_HEADER_NAME = 'X-Market-Code'
export const MARKET_COOKIE_MAX_AGE_DAYS = 365

export const multiMarketIsEnabled = (): boolean =>
  process.env.VUE_APP_MULTI_MARKET_ENABLED === 'true'

export const normalizeMarketCode = (code: string): string => code.trim().toLowerCase()

export const validateMarkets = (data: unknown): Market[] => {
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error('The market configuration could not be loaded.')
  }

  const codes = new Set<string>()
  return data.map(item => {
    if (!item || typeof item.code !== 'string' || !item.code.trim()
      || typeof item.name !== 'string' || !item.name.trim()
      || typeof item.default_currency !== 'string' || !/^[A-Z]{3}$/.test(item.default_currency)
      || !Array.isArray(item.allowed_currencies)
      || !item.allowed_currencies.every((currency: unknown) => typeof currency === 'string' && /^[A-Z]{3}$/.test(currency))
      || !item.allowed_currencies.includes(item.default_currency)
      || typeof item.loyalty_enabled !== 'boolean') {
      throw new Error('The market currency or loyalty configuration is invalid. Please retry loading markets.')
    }

    const code = normalizeMarketCode(item.code)
    if (codes.has(code)) {
      throw new Error('The market configuration contains duplicate market codes.')
    }
    codes.add(code)

    return { ...item, code }
  })
}

export const defaultMarketCode = (): string =>
  normalizeMarketCode(process.env.VUE_APP_DEFAULT_MARKET_CODE || 'no')

export const marketCookieOptions = () => ({
  domain: process.env.VUE_APP_ADMIN_MARKET_COOKIE_DOMAIN || undefined,
  expires: MARKET_COOKIE_MAX_AGE_DAYS,
  path: '/',
  sameSite: 'lax' as const,
  secure: true,
})

export const readMarketCookie = (): string | undefined => {
  const value = Cookies.get(MARKET_COOKIE_NAME)

  return value ? normalizeMarketCode(value) : undefined
}

export const persistMarketCookie = (code: string): void => {
  Cookies.set(MARKET_COOKIE_NAME, normalizeMarketCode(code), marketCookieOptions())
}

export const resolveMarketSelection = (
  markets: Market[],
  configuredDefault: string,
  savedCode?: string,
): MarketSelection => {
  const normalizedDefault = normalizeMarketCode(configuredDefault)
  const fallback = markets.find(market => market.code === normalizedDefault)

  if (!fallback) {
    throw new Error(`The configured default market "${normalizedDefault}" is unavailable.`)
  }

  const normalizedSaved = savedCode ? normalizeMarketCode(savedCode) : undefined
  const saved = markets.find(market => market.code === normalizedSaved)

  return {
    market: saved || fallback,
    fallbackApplied: Boolean(normalizedSaved && !saved),
  }
}

export const isMarketContextError = (status: number, data: unknown): boolean => {
  if (status !== 400 || typeof data !== 'object' || data === null) {
    return false
  }

  const message = 'message' in data && typeof (data as { message?: unknown }).message === 'string'
    ? (data as { message: string }).message
    : ''

  return message === 'The selected market is invalid.'
    || message === 'The market header conflicts with the request host.'
}

const withoutTrailingSlash = (value: string): string => value.replace(/\/$/, '')

export const activeMarketClientUrl = (code: string): string => {
  const value = normalizeMarketCode(code) === 'ie'
    ? process.env.VUE_APP_MARKET_IE_CLIENT_URL || 'https://youwish.ie'
    : process.env.VUE_APP_MARKET_NO_CLIENT_URL || process.env.VUE_APP_APP_URL || ''

  return withoutTrailingSlash(value)
}

export const activeMarketApiWebUrl = (code: string): string => {
  const value = normalizeMarketCode(code) === 'ie'
    ? process.env.VUE_APP_MARKET_IE_API_WEB_URL || 'https://youwish.ie/api'
    : process.env.VUE_APP_MARKET_NO_API_WEB_URL || process.env.VUE_APP_API_URL || ''

  return withoutTrailingSlash(value)
}

export const shouldWarnBeforeMarketSwitch = (path: string): boolean => {
  const alwaysWarnPrefixes = [
    '/dashboard/products',
    '/dashboard/product-variations',
    '/dashboard/orders',
    '/dashboard/pending-reviews',
    '/dashboard/suppliers',
    '/dashboard/csvcodes',
    '/dashboard/csv-codes-containers',
    '/dashboard/countries',
    '/dashboard/users',
    '/dashboard/invoices',
    '/dashboard/giftcards',
  ]

  if (alwaysWarnPrefixes.some(prefix => path.startsWith(prefix))) {
    return true
  }

  return path.includes('/new') || path.includes('/edit')
}

export const safeMarketSwitchUrl = (path: string): string => {
  const v3Base = withoutTrailingSlash(process.env.VUE_APP_V3_DASHBOARD_URL || '')

  if (path.startsWith('/dashboard/suppliers-requests')) {
    return `${v3Base}/suppliers-requests`
  }

  if (path.startsWith('/dashboard/product-variations') || path.startsWith('/dashboard/products')) {
    return `${v3Base}/products`
  }

  if (path.startsWith('/dashboard/suppliers')) {
    return `${v3Base}/suppliers`
  }

  const legacyListRoutes = [
    '/dashboard/toplists',
    '/dashboard/pages',
    '/dashboard/inspirations',
    '/dashboard/categories',
  ]
  const listRoute = legacyListRoutes.find(route => path.startsWith(`${route}/`))

  return listRoute || path
}
